import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import ArrowLeft from '@jwp/ott-theme/assets/icons/arrow_backward.svg?react';
import ArrowUp from '@jwp/ott-theme/assets/icons/Arrow_up.svg?react';
import ArrowDown from '@jwp/ott-theme/assets/icons/Arrow_down.svg?react';
import { useTranslation } from 'react-i18next';
import PlayIcon from '@jwp/ott-theme/assets/icons/play.svg?react';
import PauseIcon from '@jwp/ott-theme/assets/icons/pause.svg?react';
import ExpandIcon from '@jwp/ott-theme/assets/icons/expand.svg?react';
import MinimizeIcon from '@jwp/ott-theme/assets/icons/minimize.svg?react';
import VolumeUpIcon from '@jwp/ott-theme/assets/icons/audio-on.svg?react';
import VolumeOffIcon from '@jwp/ott-theme/assets/icons/audio-off.svg?react';
import DefaultAvatar from '@jwp/ott-theme/assets/profiles/avatar.png';
import env from '@jwp/ott-common/src/env';
import Icon from '../Icon/Icon';
import IconButton from '../IconButton/IconButton';
import ProfileCircle from '../ProfileCircle/ProfileCircle';
import CollapsibleText from '../CollapsibleText/CollapsibleText';
import Player from '../Player/Player';
import VideoPlayer from '../VideoPlayer/VideoPlayer';
import { useIsCustomPlayerEnabled } from '@jwp/ott-hooks-react/src/useAppConfig';
import useMedia from '@jwp/ott-hooks-react/src/useMedia';
import { formatTime } from '../../utils/formatTime';
import Spinner from '../Spinner/Spinner';

import styles from './VideoPreview.module.scss';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { useNavigate, useLocation } from 'react-router';
import { renderToString } from 'react-dom/server';
import type { PlaylistItem, videoDetails } from '@jwp/ott-common/types/playlist';
import type { JWPlayer } from '../../../types/jwplayer';
import { useMutation } from '@apollo/client/react';
import { LIKE_MEDIAS, UNLIKE_MEDIAS } from '@jwp/ott-common/src/graphql/mutation/reels.mutation';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import ShareButton from '../ShareButton/ShareButton';
import { shareMediaURL, channelURL } from '@jwp/ott-common/src/utils/urlFormatting';
import LikeIcon from '@jwp/ott-theme/assets/icons/component-icons/LikeIcon';
import FlagIcon from '@jwp/ott-theme/assets/icons/Flag.svg?react';
import FollowIcon from '@jwp/ott-theme/assets/icons/FollowIcon.svg?react';
import UnfollowIcon from '@jwp/ott-theme/assets/icons/UnfollowIcon.svg?react';
import ReportChannelModal from '../ReportsModal/ReportChannelModal';
import { useFollowChannel } from '../../hooks/useFollowChannel';

type PreviewChannelInfo = {
  chId: string;
  chName: string;
  chHandle?: string;
  chAvatarUrl?: string | null;
  chBannerUrl?: string | null;
  isFollowedByMe?: boolean | null;
  followersCount?: number | null;
};

type Props = {
  file?: File | null;
  previewUrl?: string | null;
  poster?: string;
  onClose: () => void;
  handleReportSuccess?: () => void;
  title?: string;
  description?: string;
  category?: string;
  author?: {
    name: string | null;
    avatarUrl: string | null;
  };
  channelInfo?: PreviewChannelInfo | null;
  showDetails?: boolean;
  isReel?: boolean;
  primaryMetadata?: React.ReactNode;
  item: PlaylistItem | videoDetails;
  reconnectingLabel?: string;
  onNextReel?: () => void;
  onPrevReel?: () => void;
  canGoNext?: boolean;
  canGoPrev?: boolean;
  isShareEnabled?: boolean;
  setChannelReels?: React.Dispatch<React.SetStateAction<any[]>>;
};

const isHlsPreviewUrl = (url?: string | null): boolean => !!url && /\.m3u8($|\?)/i.test(url);

const toAbsoluteMediaUrl = (url?: string | null): string | null => {
  if (!url) {
    return null;
  }
  if (/^(https?:|blob:|data:)/i.test(url)) {
    return url;
  }
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return url;
  }
};

type ChannelPreviewItem = PlaylistItem & {
  maJwMediaId?: string;
  maId?: string;
  maTitle?: string;
  maDescription?: string;
  maDuration?: number | string;
};

const resolveChannelJwMediaId = (previewItem?: ChannelPreviewItem) =>
  previewItem?.maJwMediaId || previewItem?.mediaid || previewItem?.maId || '';

const VideoPreview = ({
  file,
  previewUrl,
  poster,
  onClose,
  handleReportSuccess,
  title,
  description,
  category,
  author,
  channelInfo,
  showDetails = true,
  isReel = true,
  primaryMetadata,
  item,
  reconnectingLabel,
  onNextReel,
  onPrevReel,
  canGoNext = true,
  canGoPrev = true,
  isShareEnabled = true,
  setChannelReels
}: Props) => {
  const { t } = useTranslation(['upload', 'video', 'user']);
  const isCustomPlayerEnabled = useIsCustomPlayerEnabled();
  const videoRef = useRef<HTMLVideoElement>(null);
  const jwPlayerRef = useRef<JWPlayer>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [volume, setVolume] = useState(1);
  const [showVolumeBar, setShowVolumeBar] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const location = useLocation();
  const userId = useAccountStore(({ user }) => user?.id ?? null);
  const isLoggedIn = !!userId;
  const isReported = (item as any)?.isReported || false;
  const myChannelHandle = useAccountStore((state) => state.myChannelId);

  // GraphQL mutations
  const authClient = getAuthApolloClient();
  const [likeMedias] = useMutation(LIKE_MEDIAS, { client: authClient });
  const [unlikeMedias] = useMutation(UNLIKE_MEDIAS, { client: authClient });

  const searchParams = new URLSearchParams(location.search);
  const minimalView = searchParams.get('minimalView') === 'true';
  const metadataString = primaryMetadata ? renderToString(primaryMetadata) : '';
  const isHlsSource = isHlsPreviewUrl(previewUrl);
  const useCustomPlayerUi = isCustomPlayerEnabled && !isReel;
  const previewItem = item as ChannelPreviewItem | undefined;
  const jwMediaId = resolveChannelJwMediaId(previewItem);
  const { data: jwMediaItem, isLoading: isJwMediaLoading } = useMedia(jwMediaId, true);
  const shouldWaitForJwMedia = useCustomPlayerUi && Boolean(jwMediaId);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  useEffect(() => {
    if (item) {
      setIsLiked(!!item.isLikedByUser);
      setLikeCount((item.likeCount as number) || 0);
    }
  }, [item]);

  const hlsPlaylistItem = useMemo(() => {
    if (!isHlsSource || !previewUrl) { return null; }

    return {
      mediaid: (item as PlaylistItem)?.mediaid || 'broadcast-hls',
      title: title || '',
      description: description || '',
      image: poster || (item as PlaylistItem)?.image,
      file: previewUrl,
      sources: [{ file: previewUrl, type: 'hls' as const }],
      mediaStatus: 'VOD' as const,
      duration: (item as PlaylistItem)?.duration || 0,
      allSources: undefined,
    };
  }, [isHlsSource, previewUrl, item, title, description, poster]);

  const customPlayerItem = useMemo(() => {
    if (jwMediaItem) {
      return {
        ...jwMediaItem,
        title: title || jwMediaItem.title,
        description: description || jwMediaItem.description,
        image: poster || jwMediaItem.image,
      };
    }

    const fileUrl = toAbsoluteMediaUrl(previewUrl || videoUrl);
    if (!fileUrl) {
      return null;
    }

    const isHls = isHlsPreviewUrl(fileUrl);
    const durationValue = Number(previewItem?.duration || previewItem?.maDuration || 0);

    return {
      mediaid: jwMediaId || previewItem?.mediaid || previewItem?.maId || 'channel-video',
      title: title || previewItem?.title || previewItem?.maTitle || '',
      description: description || previewItem?.description || previewItem?.maDescription || '',
      image: poster || previewItem?.image,
      file: fileUrl,
      sources: [{ file: fileUrl, type: isHls ? 'hls' : 'mp4' }],
      tracks: jwMediaItem?.tracks,
      mediaStatus: 'VOD' as const,
      duration: Number.isFinite(durationValue) ? durationValue : 0,
      allSources: undefined,
    } as PlaylistItem;
  }, [jwMediaItem, jwMediaId, previewUrl, videoUrl, previewItem, title, description, poster]);

  const handleJwReady = useCallback((player?: JWPlayer) => {
    if (!player) { return; }

    jwPlayerRef.current = player;
    // isLoading intentionally NOT set here — the spinner should stay visible until the
    // video actually has a frame to show (onFirstFrame) or starts playing (onPlay),
    // not just when the player instance itself is ready.
    setIsPlaying(player.getState() === 'playing');
    setDuration(player.getDuration() || 0);
    setCurrentTime(player.getPosition() || 0);
    setVolume((player.getVolume?.() ?? 100) / 100);
    setIsMuted(player.getMute?.() ?? false);

    const onTime = ({ position }: { position: number }) => setCurrentTime(position);
    const onMeta = () => setDuration(player.getDuration() || 0);
    const onPlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
    };
    const onPause = () => setIsPlaying(false);
    const onComplete = () => setIsPlaying(false);
    const onBuffer = () => setIsLoading(true);
    const onFirstFrame = () => setIsLoading(false);

    player.on('time', onTime);
    player.on('meta', onMeta);
    player.on('play', onPlay);
    player.on('pause', onPause);
    player.on('complete', onComplete);
    player.on('buffer', onBuffer);
    player.on('firstFrame', onFirstFrame);
  }, []);

  const navigate = useNavigate();
  const showMoreClickHandler = () => {
    const newState = { item, primaryMetadata: metadataString };

    sessionStorage.setItem('seeMoreDetails', JSON.stringify(newState));

    navigate(modalURLFromLocation(location, 'show-more-details'), {
      state: newState,
    });
  };


  const handleLikeToggle = useCallback(async () => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    const mediaId = (item as PlaylistItem).maId as string;
    if (!mediaId) return;

    const newIsLiked = !isLiked;
    const newCount = Math.max(0, likeCount + (newIsLiked ? 1 : -1));

    // Optimistic update - local state only
    setIsLiked(newIsLiked);
    setLikeCount(newCount);

    // 👇 Also update in channelReels array
    if (setChannelReels) {
      setChannelReels((prev) =>
        prev.map((reel: any) =>
          reel.maId === mediaId
            ? { ...reel, isLikedByUser: newIsLiked, likeCount: newCount }
            : reel
        )
      );
    }

    try {
      if (newIsLiked) {
        await likeMedias({
          variables: { mediaIds: [mediaId] },
          refetchQueries: [], // 👈 Don't refetch
        });
      } else {
        await unlikeMedias({
          variables: { mediaIds: [mediaId] },
          refetchQueries: [], // 👈 Don't refetch
        });
      }
    } catch (error) {
      console.error('Like toggle failed:', error);
      // Revert on error
      setIsLiked(!newIsLiked);
      setLikeCount(Math.max(0, newCount + (newIsLiked ? -1 : 1)));
      if (setChannelReels) {
        setChannelReels((prev) =>
          prev.map((reel: any) =>
            reel.maId === mediaId
              ? { ...reel, isLikedByUser: !newIsLiked, likeCount: Math.max(0, newCount + (newIsLiked ? -1 : 1)) }
              : reel
          )
        );
      }
    }
  }, [isLoggedIn, isLiked, likeCount, item, navigate, likeMedias, unlikeMedias]);


  useEffect(() => {
    if (previewUrl) {
      setVideoUrl(previewUrl);
      return;
    }
    if (file) {
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setVideoUrl(null);
  }, [file, previewUrl]);

  const togglePlay = () => {
    if (isHlsSource && jwPlayerRef.current) {
      const state = jwPlayerRef.current.getState();
      if (state === 'playing') {
        jwPlayerRef.current.pause();
      } else {
        jwPlayerRef.current.play();
      }
      return;
    }

    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (isHlsSource && jwPlayerRef.current) {
      const newMuted = !isMuted;
      jwPlayerRef.current.setMute(newMuted);
      setIsMuted(newMuted);
      if (!newMuted && volume === 0) {
        setVolume(1);
        jwPlayerRef.current.setVolume(100);
      }
      return;
    }

    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
      if (!newMuted && volume === 0) {
        setVolume(1);
        videoRef.current.volume = 1;
      }
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) { return; }

    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    } else {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
      setIsFullscreen(true);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const handleWaiting = () => setIsLoading(true);
  const handlePlaying = () => setIsLoading(false);
  const handleCanPlay = () => setIsLoading(false);

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number.parseFloat(e.target.value);

    if (isHlsSource && jwPlayerRef.current) {
      jwPlayerRef.current.seek(time);
      setCurrentTime(time);
      return;
    }

    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number.parseFloat(e.target.value);
    setVolume(val);

    if (isHlsSource && jwPlayerRef.current) {
      jwPlayerRef.current.setVolume(val * 100);
      if (val > 0 && isMuted) {
        jwPlayerRef.current.setMute(false);
        setIsMuted(false);
      } else if (val === 0 && !isMuted) {
        jwPlayerRef.current.setMute(true);
        setIsMuted(true);
      }
      return;
    }

    if (videoRef.current) {
      videoRef.current.volume = val;
      if (val > 0 && isMuted) {
        videoRef.current.muted = false;
        setIsMuted(false);
      } else if (val === 0 && !isMuted) {
        videoRef.current.muted = true;
        setIsMuted(true);
      }
    }
  };

  const resolvedChannel: PreviewChannelInfo | undefined =
    channelInfo || (item?.channelInfo as PreviewChannelInfo | undefined);
  const currentChId = resolvedChannel?.chId;

  const isOwner = useMemo(() => {
    if (!myChannelHandle || !currentChId) {
      return false;
    }

    return myChannelHandle === currentChId;
  }, [myChannelHandle, currentChId]);

  const { isFollowing, isBusy: isFollowBusy, toggleFollow, hydrate } = useFollowChannel(currentChId);

  useEffect(() => {
    if (currentChId) {
      hydrate(currentChId, resolvedChannel?.isFollowedByMe ?? (item?.isFollowing as boolean | undefined));
    }
  }, [currentChId, hydrate, resolvedChannel?.isFollowedByMe, item?.isFollowing]);

  const displayedFollowers = useMemo(() => {
    const apiFollowers = resolvedChannel?.followersCount ?? 0;
    const apiFollowing = !!resolvedChannel?.isFollowedByMe;
    if (apiFollowing === isFollowing) {
      return apiFollowers;
    }
    return isFollowing ? apiFollowers + 1 : Math.max(0, apiFollowers - 1);
  }, [isFollowing, resolvedChannel?.followersCount, resolvedChannel?.isFollowedByMe]);

  const channelAvatarSrc = resolvedChannel?.chAvatarUrl
    ? `${env.APP_BASE_URL || ''}${resolvedChannel.chAvatarUrl}`
    : author?.avatarUrl || DefaultAvatar;

  return (
    <div className={classNames(styles.previewOverlay, { [styles.customPlayerOverlay]: useCustomPlayerUi })} ref={containerRef}>
      {!useCustomPlayerUi && (
        <div className={styles.header} style={{ alignItems: showDetails ? 'flex-start' : 'center' }}>
          <IconButton
            onClick={(e) => {
              e?.stopPropagation();
              e?.preventDefault();
              onClose();
            }}
            className={styles.backBtn}
          >
            <Icon icon={ArrowLeft} />
          </IconButton>
          <div className={styles.headerContent}>
            {title && <h2 className={styles.previewTitle}>{title}</h2>}
            <div className={styles.metaContainer}>
              {showDetails && isReel && description && !minimalView && (
                <CollapsibleText item={item} text={description} className={styles.description} maxWords={12} showMoreClickHandler={showMoreClickHandler} />
              )}
              {showDetails && isReel && category && !minimalView && <span className={classNames(styles.categoryBadge, styles.mobileCategory)}>{category}</span>}
              {showDetails && isReel && !minimalView && (resolvedChannel || author?.name) && (
                <div
                  className={styles.authorContainer}
                  onClick={() => {
                    if (currentChId) {
                      navigate(channelURL(currentChId));
                    }
                  }}
                >
                  <ProfileCircle
                    src={channelAvatarSrc}
                    alt={resolvedChannel?.chName || author?.name || ''}
                    className={styles.authorAvatar}
                  />
                  <div className={styles.channelInfo}>
                    <span className={styles.authorName}>{resolvedChannel?.chName || author?.name}</span>
                    {typeof resolvedChannel?.followersCount === 'number' && (
                      <span className={styles.followersCount}>
                        {displayedFollowers} {t('user:channel.followers_label', { defaultValue: 'Followers' })}
                      </span>
                    )}
                  </div>
                  {!isOwner && currentChId && (
                    <button
                      type="button"
                      className={classNames(styles.reelFollowButton, isFollowing ? styles.reelUnfollowButton : styles.reelFollowFilledButton)}
                      onClick={(event) => {
                        event.stopPropagation();
                        void toggleFollow();
                      }}
                      disabled={isFollowBusy}
                      aria-label={isFollowing ? t('user:channel.unfollow_label') : t('user:channel.follow_label')}
                    >
                      <Icon icon={isFollowing ? UnfollowIcon : FollowIcon} className={styles.reelFollowIcon} />
                      {isFollowing ? t('user:channel.unfollow_label') : t('user:channel.follow_label')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={styles.centerContainer}>
        <div
          className={classNames(styles.videoWrapper, { [styles.horizontalWrapper]: !isReel })}
          onClick={useCustomPlayerUi ? undefined : togglePlay}
          onKeyDown={useCustomPlayerUi ? undefined : (e) => e.key === ' ' && togglePlay()}
          aria-label={t('upload_box.preview_alt')}
        >
          {shouldWaitForJwMedia && isJwMediaLoading ? (
            <div className={styles.loadingOverlay}>
              <Spinner size="medium" />
            </div>
          ) : useCustomPlayerUi && customPlayerItem ? (
            <div className={styles.hlsPlayerShell}>
              <VideoPlayer
                key={customPlayerItem.mediaid}
                item={customPlayerItem}
                autostart
                disableWatchHistory
                onClose={onClose}
                onReady={handleJwReady}
                ReConnecting={reconnectingLabel || t('video:reconnecting', { defaultValue: 'Reconnecting...' })}
                config={{
                  callbacks: {
                    onBack: onClose,
                    onLike: handleLikeToggle,
                  },
                }}
              />
            </div>
          ) : isHlsSource && hlsPlaylistItem ? (
            <div className={styles.hlsPlayerShell}>
              <Player
                key={hlsPlaylistItem.mediaid}
                item={hlsPlaylistItem as PlaylistItem}
                autostart
                disableWatchHistory
                onReady={handleJwReady}
                onClose={onClose}
                ReConnecting={reconnectingLabel || t('video:reconnecting', { defaultValue: 'Reconnecting...' })}
              />
            </div>
          ) : videoUrl ? (
            <video
              ref={videoRef}
              src={videoUrl}
              className={styles.video}
              poster={poster}
              muted={isMuted}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleEnded}
              onWaiting={handleWaiting}
              onPlaying={handlePlaying}
              onCanPlay={handleCanPlay}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              autoPlay
              playsInline
              disablePictureInPicture
            >
              <track kind="captions" />
            </video>
          ) : null}

          {!useCustomPlayerUi && !isPlaying && isReel && !isLoading && (
            <div className={styles.playOverlay}>
              <Icon icon={PlayIcon} className={styles.largePlayIcon} />
            </div>
          )}

          {!useCustomPlayerUi && isLoading && (
            <div className={styles.loadingOverlay}>
              <Spinner size="medium" />
            </div>
          )}

          {isReel && !isLoading && (
            <div
              className={styles.topRightControls}
              onMouseEnter={() => setShowVolumeBar(true)}
              onMouseLeave={() => setShowVolumeBar(false)}
              onClick={(e) => e.stopPropagation()}
            >
              {showVolumeBar && (
                <div className={styles.volumeBarContainer} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className={styles.volumeSlider}
                    style={{ '--percent': `${(isMuted ? 0 : volume) * 100}%` } as React.CSSProperties}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
              <IconButton
                onClick={(e) => {
                  e?.stopPropagation();
                  toggleMute();
                }}
                className={styles.muteBtn}
              >
                <Icon icon={isMuted || volume === 0 ? VolumeOffIcon : VolumeUpIcon} />
              </IconButton>
            </div>
          )}

          {showDetails && isReel && category && !isLoading && !minimalView && (
            <div className={styles.topLeftControls}>
              <span className={styles.categoryBadge}>{category}</span>
            </div>
          )}

          {!useCustomPlayerUi && !isLoading && (
            <div className={styles.bottomControls} onClick={(e) => e.stopPropagation()}>
              <div className={styles.progressBarContainer}>
                <input
                  type="range"
                  className={styles.scrubber}
                  style={{ '--percent': `${(currentTime / (duration || 1)) * 100}%` } as React.CSSProperties}
                  min={0}
                  max={duration || 0}
                  step={0.1}
                  value={currentTime}
                  onChange={handleSeek}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {!isReel && (
                <div className={styles.controlsRow}>
                  <div className={styles.leftControls}>
                    <IconButton
                      onClick={(e) => {
                        e?.stopPropagation();
                        togglePlay();
                      }}
                      className={classNames(styles.controlBtn, { [styles.whiteControl]: !isReel })}
                    >
                      {isPlaying ? <Icon icon={PauseIcon} /> : <Icon icon={PlayIcon} />}
                    </IconButton>
                    <div
                      className={styles.volumeControlWrapper}
                      onMouseEnter={() => setShowVolumeBar(true)}
                      onMouseLeave={() => setShowVolumeBar(false)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {showVolumeBar && (
                        <div className={styles.volumeBarContainerHorizontal} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={isMuted ? 0 : volume}
                            onChange={handleVolumeChange}
                            className={styles.volumeSlider}
                            style={{ '--percent': `${(isMuted ? 0 : volume) * 100}%` } as React.CSSProperties}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                      <IconButton
                        onClick={(e) => {
                          e?.stopPropagation();
                          toggleMute();
                        }}
                        className={styles.controlBtn}
                      >
                        <Icon icon={isMuted || volume === 0 ? VolumeOffIcon : VolumeUpIcon} />
                      </IconButton>
                    </div>
                    <div className={styles.timeInfo}>
                      <span>{formatTime(Math.floor(currentTime))}</span>
                      <span className={styles.timeDivider}>/</span>
                      <span>{formatTime(Math.floor(duration))}</span>
                    </div>
                  </div>
                  <div className={styles.rightControls}>
                    <IconButton
                      onClick={(e) => {
                        e?.stopPropagation();
                        toggleFullscreen();
                      }}
                      className={styles.controlBtn}
                    >
                      {isFullscreen ? <Icon icon={MinimizeIcon} /> : <Icon icon={ExpandIcon} />}
                    </IconButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {isReel && (
          <div className={styles.sideActions}>
            {!isReported && <div className={styles.actionItem}>
              <button
                className={classNames(styles.actionBtn, { [styles.liked]: isLiked })}
                onClick={handleLikeToggle}
                aria-label={isLiked ? 'Unlike Video' : 'Like Video'}
              >
                <LikeIcon isLiked={isLiked} fillColor="#ff9f1c" width={24} height={24} />
              </button>
              <span className={styles.actionLabel}>{likeCount}</span>
            </div>}

            {isShareEnabled && (
              <div className={styles.shareBtnWrapper}>
                {!isReported && <> <ShareButton
                  title={title || ''}
                  description={description || ''}
                  mediaId={item.maJwMediaId}
                  url={
                    item.maJwMediaId
                      ? `${window.location.origin}${shareMediaURL(item.maJwMediaId as string, item.playlistId as string | null)}`
                      : window.location.href
                  }
                />
                  <span className={styles.actionLabel}>Share</span>
                </>}
              </div>
            )}

            {!isOwner && (
              <div className={styles.actionItem}>
                <button
                  className={classNames(styles.actionBtn)}
                  onClick={() => setReportModalOpen(true)}
                  aria-label={'Report Reel'}
                >
                  <Icon icon={FlagIcon} className={styles.flagIcon} />
                </button>
                <span className={styles.actionLabel}>Report</span>
              </div>
            )}
          </div>
        )}
      </div>
      {isReel && (
        <div className={styles.rightPanel}>
          <div className={styles.navigationArrows}>
            <IconButton
              className={classNames(styles.backBtn, styles.arrowBtn, { [styles.disabled]: !canGoPrev })}
              onClick={onPrevReel}
              disabled={!canGoPrev}
              aria-label="Previous Video"
            >
              <Icon icon={ArrowUp} />
            </IconButton>
            <IconButton
              className={classNames(styles.backBtn, styles.arrowBtn, { [styles.disabled]: !canGoNext })}
              onClick={onNextReel}
              disabled={!canGoNext}
              aria-label="Next Video"
            >
              <Icon icon={ArrowDown} />
            </IconButton>
          </div>
        </div>
      )}

      {reportModalOpen && (
        <ReportChannelModal
          mediaId={String(item?.maId) || ''}
          onClose={() => setReportModalOpen(false)}
          onSuccess={() => {
            if (handleReportSuccess) {
              handleReportSuccess();
            }
          }}
        />
      )}
    </div>
  );
};

export default VideoPreview;
